import { useState } from 'react';
import { Packet, PacketStatus, Decision, RoleType } from '@promo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { post, put } from '@/lib/api';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/use-toast';
import { getDistinctPacketStatusLabel } from '@/lib/packetStatus';
import { WithdrawPacketDialog } from './WithdrawPacketDialog';
import { useNavigate } from 'react-router-dom';

interface StatusUpdateButtonsProps {
  packet: Packet;
  currentUserRole: RoleType | '';
  className?: string;
  currentUserID: string;
  managerIds?: string[];
  invitedAssessorCount: number;
  completedBlindAssessmentCount: number;
}

export function StatusUpdateButtons({ packet, currentUserRole, className, currentUserID, managerIds, invitedAssessorCount, completedBlindAssessmentCount }: StatusUpdateButtonsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const navigate = useNavigate();

  const updateStatusMutation = useMutation<Packet, Error, { status: PacketStatus }>({
    mutationFn: async (newStatus) => put(`/packets/${packet.id}`, newStatus),
    onSuccess: (updatedPacket) => {
      queryClient.invalidateQueries({ queryKey: ['packet', packet.id] });
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      toast({ title: "Status Updated", description: `Packet moved to ${getDistinctPacketStatusLabel(updatedPacket.status)}` });
    },
    onError: (error) => {
      toast({ title: "Error", description: `Failed to update status: ${error.message}`, variant: "destructive" });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (data: { withdrawalJustification: string }) => 
      put(`/packets/${packet.id}`, { 
        status: PacketStatus.WITHDRAWN,
        withdrawalJustification: data.withdrawalJustification 
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packet', packet.id] });
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      toast({
        title: 'Success',
        description: 'Promotion packet withdrawn successfully',
      });
      setIsWithdrawDialogOpen(false);
    },
    onError: (error) => {
      console.error('Failed to withdraw packet:', error);
      toast({
        title: 'Error',
        description: 'Failed to withdraw promotion packet',
        variant: 'destructive',
      });
    },
  });

  // Mutation to create a re-assessment (new attempt)
  const reassessMutation = useMutation<Packet, Error, void>({
    mutationFn: async () => post<Packet>(`/packets/${packet.id}/reassess`, {}),
    onSuccess: (newPacket) => {
      // Prime cache so PacketPage has data immediately
      queryClient.setQueryData(['packet', newPacket.id], newPacket);
      queryClient.invalidateQueries({ queryKey: ['packets'] });
      toast({
        title: 'Re-assessment started',
        description: 'A new packet has been created.',
      });
      navigate(`/packets/${newPacket.id}`);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleBeginBlindAssessment = () => {
    if (invitedAssessorCount !== 3 && invitedAssessorCount !== 5) {
      toast({ 
        title: "Cannot start assessment", 
        description: `You must have exactly 3 or 5 assessors invited. Currently have ${invitedAssessorCount}.`,
        variant: "destructive" 
      });
      return;
    }
    updateStatusMutation.mutate({ status: PacketStatus.BLINDED_ASSESSMENT });
  };

  const handleBeginUnblindedAssessment = () => {
    if (invitedAssessorCount !== 3 && invitedAssessorCount !== 5) {
      toast({ 
        title: "Cannot start assessment", 
        description: `You must have exactly 3 or 5 assessors invited. Currently have ${invitedAssessorCount}.`,
        variant: "destructive" 
      });
      return;
    }
    updateStatusMutation.mutate({ status: PacketStatus.UNBLINDED_ASSESSMENT });
  };

  const handleSendToSeniorLeader = () => {
    updateStatusMutation.mutate({ status: PacketStatus.SENIOR_LEADER_REVIEW });
  };

  const handleSendToHR = () => {
    updateStatusMutation.mutate({ status: PacketStatus.HR_REVIEW });
  };

  const handleWithdrawConfirm = (justification: string) => {
    withdrawMutation.mutate({ withdrawalJustification: justification });
  };

  const hasExplicitManagerIds = !!managerIds?.length;
  const packetManagerIds = hasExplicitManagerIds ? managerIds : [packet.managerId];
  const isPacketManager = packetManagerIds.includes(currentUserID);
  const isPacketSeniorLeader = currentUserID === packet.seniorLeaderId;
  const canUseManagerRoleControls =
    isPacketManager || (!hasExplicitManagerIds && currentUserRole === RoleType.MANAGER);
  const canBeginBlindAssessment = canUseManagerRoleControls || isPacketSeniorLeader;
  const canUserManagePacket = canUseManagerRoleControls || isPacketSeniorLeader;

  let mainButton: React.ReactNode = null;

  if (packet.status === PacketStatus.DRAFT && canBeginBlindAssessment) {
    mainButton = (
      <Button
        onClick={handleBeginBlindAssessment}
        disabled={updateStatusMutation.isPending}
      >
        {updateStatusMutation.isPending && updateStatusMutation.variables?.status === PacketStatus.BLINDED_ASSESSMENT 
          ? <><Spinner className="mr-2 h-4 w-4" />Processing...</> 
          : 'Begin Blind Assessment'}
      </Button>
    );
  } else if (
    packet.status === PacketStatus.BLINDED_ASSESSMENT &&
    canUserManagePacket &&
    invitedAssessorCount > 0 &&
    completedBlindAssessmentCount === invitedAssessorCount
  ) {
    mainButton = (
      <Button
        onClick={handleBeginUnblindedAssessment}
        disabled={updateStatusMutation.isPending}
      >
        {updateStatusMutation.isPending && updateStatusMutation.variables?.status === PacketStatus.UNBLINDED_ASSESSMENT
          ? <><Spinner className="mr-2 h-4 w-4" />Processing...</>
          : 'Begin Unblinded Assessment'}
      </Button>
    );
  } else if (packet.status === PacketStatus.MANAGER_SUMMARY && canUserManagePacket) {
    mainButton = (
      <Button
        onClick={handleSendToSeniorLeader}
        disabled={updateStatusMutation.isPending}
      >
        {updateStatusMutation.isPending && updateStatusMutation.variables?.status === PacketStatus.SENIOR_LEADER_REVIEW
          ? <><Spinner className="mr-2 h-4 w-4" />Processing...</>
          : 'Send to Senior Leader'}
      </Button>
    );
  } else if (
    packet.status === PacketStatus.SENIOR_LEADER_REVIEW &&
    packet.ratification &&
    canUserManagePacket
  ) {
    mainButton = (
      <Button
        onClick={handleSendToHR}
        disabled={updateStatusMutation.isPending}
      >
        {updateStatusMutation.isPending && updateStatusMutation.variables?.status === PacketStatus.HR_REVIEW
          ? <><Spinner className="mr-2 h-4 w-4" />Processing...</>
          : 'Send to HR'}
      </Button>
    );
  } else if (packet.status === PacketStatus.HR_REVIEW && canUserManagePacket) {
    mainButton = (
      <div className="flex flex-col gap-2 items-end">
        <Button
          onClick={() => updateStatusMutation.mutate({ status: PacketStatus.COMPLETED })}
          disabled={updateStatusMutation.isPending}
        >
          {updateStatusMutation.isPending && updateStatusMutation.variables?.status === PacketStatus.COMPLETED ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Completing...
            </>
          ) : (
            'Complete'
          )}
        </Button>
        <div className="text-xs text-muted-foreground">
          Candidate will see decision
        </div>
      </div>
    );
  }

  const showWithdrawButton = canUserManagePacket && packet.status !== PacketStatus.WITHDRAWN;

  // Re-assessment button (manager only, latest attempt, DECISION DEFER, status COMPLETED)
  const showReassess =
    packet.status === PacketStatus.COMPLETED &&
    packet.decision === Decision.DEFER &&
    packet.isLatestAttempt &&
    canUseManagerRoleControls;

  if (!mainButton && !showWithdrawButton && !showReassess) {
    return null;
  }

  return (
    <>
      <div className={`relative w-full ${className || ''}`}>
        {showWithdrawButton && (
          <div className="absolute left-0 top-0">
            <Button
              variant="destructive"
              onClick={() => setIsWithdrawDialogOpen(true)}
              disabled={withdrawMutation.isPending}
            >
              {withdrawMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Withdrawing...
                </>
              ) : (
                'Withdraw'
              )}
            </Button>
          </div>
        )}

        <div className="flex gap-4 justify-end">
          {mainButton}
          {showReassess && (
            <Button
              variant="outline"
              onClick={() => reassessMutation.mutate()}
              disabled={reassessMutation.isPending}
            >
              {reassessMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />Starting…
                </>
              ) : (
                'Start Re-assessment'
              )}
            </Button>
          )}
        </div>
      </div>

      <WithdrawPacketDialog
        open={isWithdrawDialogOpen}
        onOpenChange={setIsWithdrawDialogOpen}
        onConfirm={handleWithdrawConfirm}
        isSubmitting={withdrawMutation.isPending}
      />
    </>
  );
}
